import type { StreamChatMutationVariables } from '@massilia/queries'

import { create } from 'zustand'
import { syncTabs } from 'zustand-sync-tabs'
import { createJSONStorage, persist } from 'zustand/middleware'

const STORAGE_KEY = 'massilia.chat.widget.v1'
const MAX_MESSAGES_TO_KEEP = 10
const MAX_MESSAGE_CHARS = 2000

export interface ChatMessage {
  content: string
  role: ChatRole
}

export type ChatRole = 'assistant' | 'user'

interface ChatWidgetStoreActions {
  sendMessage: (options: SendMessageOptions) => Promise<void>
  setError: (error: null | string) => void
  setInput: (input: string) => void
  toggleOpen: () => void
}

interface ChatWidgetStoreState {
  error: null | string
  input: string
  isOpen: boolean
  isSending: boolean
  isWaitingForFirstChunk: boolean
  messages: ChatMessage[]
}

interface SendMessageOptions {
  isNearBottom?: () => boolean
  onAssistantChunkRendered?: () => void
  streamAssistantReply: (variables: StreamChatMutationVariables) => Promise<unknown>
}

const trimMessages = (messages: ChatMessage[]) =>
  messages.length > MAX_MESSAGES_TO_KEEP ? messages.slice(-MAX_MESSAGES_TO_KEEP) : messages

export const useChatWidgetStore = create<ChatWidgetStoreActions & ChatWidgetStoreState>()(
  syncTabs(
    persist(
      (set, get) => ({
        error: null,
        input: '',
        isOpen: false,
        isSending: false,
        isWaitingForFirstChunk: false,
        messages: [],
        sendMessage: async (options) => {
          const { input, isSending, messages } = get()
          const trimmed = input.trim()
          if (!trimmed || isSending) {
            return
          }

          if (trimmed.length > MAX_MESSAGE_CHARS) {
            set({
              error:
                `Votre message est trop long (${trimmed.length} caractères). ` +
                `Merci de le raccourcir à moins de ${MAX_MESSAGE_CHARS} caractères.`,
            })
            return
          }

          const userMessage: ChatMessage = { content: trimmed, role: 'user' }
          const nextMessages = trimMessages([...messages, userMessage])

          set({
            error: null,
            input: '',
            isSending: true,
            isWaitingForFirstChunk: true,
            messages: trimMessages([...nextMessages, { content: '', role: 'assistant' }]),
          })

          try {
            let shouldAutoScrollLatest = true
            let pendingText = ''
            let pendingToRender = ''
            let rafScheduled = false

            const flushPending = (autoScroll: boolean) => {
              const textToAppend = pendingToRender
              if (!textToAppend) return
              pendingToRender = ''

              set((current) => {
                const updated = [...current.messages]
                const last = updated[updated.length - 1]
                if (last?.role === 'assistant') {
                  updated[updated.length - 1] = {
                    ...last,
                    content: last.content + textToAppend,
                  }
                } else {
                  updated.push({
                    content: textToAppend,
                    role: 'assistant',
                  })
                }
                return { messages: trimMessages(updated) }
              })

              if (autoScroll) {
                options?.onAssistantChunkRendered?.()
              }
            }

            const scheduleFlush = () => {
              if (rafScheduled) return
              rafScheduled = true
              requestAnimationFrame(() => {
                rafScheduled = false
                flushPending(shouldAutoScrollLatest)
              })
            }

            await options.streamAssistantReply({
              messages: nextMessages,
              onChunk: (chunk) => {
                if (get().isWaitingForFirstChunk) {
                  set({ isWaitingForFirstChunk: false })
                }
                shouldAutoScrollLatest = options?.isNearBottom?.() ?? true
                pendingText += chunk

                const boundaryIndex = Math.max(
                  pendingText.lastIndexOf(' '),
                  pendingText.lastIndexOf('\n'),
                  pendingText.lastIndexOf('\t'),
                  pendingText.lastIndexOf('.'),
                  pendingText.lastIndexOf(','),
                  pendingText.lastIndexOf('!'),
                  pendingText.lastIndexOf('?'),
                  pendingText.lastIndexOf(':'),
                  pendingText.lastIndexOf(';')
                )

                if (boundaryIndex >= 0) {
                  pendingToRender += pendingText.slice(0, boundaryIndex + 1)
                  pendingText = pendingText.slice(boundaryIndex + 1)
                  scheduleFlush()
                }
              },
            })

            if (pendingText) {
              pendingToRender += pendingText
              pendingText = ''
            }
            flushPending(shouldAutoScrollLatest)
          } catch (err) {
            set((current) => {
              const last = current.messages[current.messages.length - 1]
              return {
                error:
                  err instanceof Error ? err.message : "Une erreur s'est produite lors de l'envoi.",
                isWaitingForFirstChunk: false,
                messages:
                  last?.role === 'assistant' && last.content === ''
                    ? current.messages.slice(0, -1)
                    : current.messages,
              }
            })
          } finally {
            set({ isSending: false, isWaitingForFirstChunk: false })
          }
        },
        setError: (error) => set({ error }),
        setInput: (input) => set({ input }),
        toggleOpen: () => set((state) => ({ error: null, isOpen: !state.isOpen })),
      }),
      {
        name: STORAGE_KEY,
        partialize: (state) => ({
          isOpen: state.isOpen,
          messages: trimMessages(state.messages),
        }),
        storage: createJSONStorage(() => localStorage),
      }
    ),
    { name: STORAGE_KEY }
  )
)
